/**
 * Thin WebSocket wrapper that the adapter / dispatcher hand frames to.
 * The actual frame pairing lives in CallDispatcher; this class only handles
 * the bytes-on-the-wire concerns (subprotocol negotiation, JSON serialise/
 * deserialise, open/close lifecycle).
 *
 * The WebSocket constructor is injectable so tests can drop in a mock.
 */
import { OcppFrameParseError } from '../errors';
import type { OcppFrame } from '../OcppCall';

export interface WebSocketLike {
  readonly readyState: number;
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev?: unknown) => void) | null;
  onclose: ((ev?: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onerror: ((ev?: unknown) => void) | null;
}

export type WebSocketFactory = (url: string, subprotocol: string) => WebSocketLike;

export const SUBPROTOCOL_BY_VERSION: Record<string, string> = {
  '1.6J': 'ocpp1.6',
  '2.0.1': 'ocpp2.0.1',
  '2.1': 'ocpp2.1',
};

export interface OcppWebSocketTransportOptions {
  readonly url: string;
  readonly subprotocol: string;
  readonly factory?: WebSocketFactory;
  readonly onFrame: (frame: OcppFrame) => void;
  readonly onOpen?: () => void;
  readonly onClose?: (code: number | undefined, reason: string | undefined) => void;
  readonly onError?: (err: unknown) => void;
}

export class OcppWebSocketTransport {
  readonly #url: string;
  readonly #subprotocol: string;
  readonly #factory: WebSocketFactory;
  readonly #onFrame: (frame: OcppFrame) => void;
  readonly #onOpen?: () => void;
  readonly #onClose?: (code: number | undefined, reason: string | undefined) => void;
  readonly #onError?: (err: unknown) => void;
  #socket: WebSocketLike | null = null;

  constructor(opts: OcppWebSocketTransportOptions) {
    this.#url = opts.url;
    this.#subprotocol = opts.subprotocol;
    this.#factory = opts.factory ?? defaultFactory;
    this.#onFrame = opts.onFrame;
    if (opts.onOpen) this.#onOpen = opts.onOpen;
    if (opts.onClose) this.#onClose = opts.onClose;
    if (opts.onError) this.#onError = opts.onError;
  }

  get isOpen(): boolean {
    return this.#socket?.readyState === 1;
  }

  connect(): void {
    if (this.#socket) return;
    const sock = this.#factory(this.#url, this.#subprotocol);
    sock.onopen = () => this.#onOpen?.();
    sock.onclose = (ev) => {
      const closeEv = ev as { code?: number; reason?: string } | undefined;
      this.#socket = null;
      this.#onClose?.(closeEv?.code, closeEv?.reason);
    };
    sock.onerror = (err) => this.#onError?.(err);
    sock.onmessage = (ev) => {
      let parsed: unknown;
      try {
        parsed = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data;
      } catch (err) {
        this.#onError?.(new OcppFrameParseError(`JSON parse error: ${(err as Error).message}`));
        return;
      }
      this.#onFrame(parsed as OcppFrame);
    };
    this.#socket = sock;
  }

  send(frame: OcppFrame): void {
    if (!this.#socket) throw new Error('OcppWebSocketTransport: not connected');
    this.#socket.send(JSON.stringify(frame));
  }

  close(code = 1000, reason = ''): void {
    this.#socket?.close(code, reason);
    this.#socket = null;
  }
}

function defaultFactory(url: string, subprotocol: string): WebSocketLike {
  if (typeof globalThis === 'undefined' || !('WebSocket' in globalThis)) {
    throw new Error('WebSocket not available — pass a factory explicitly');
  }
  const Ctor = (globalThis as { WebSocket: new (u: string, p: string) => WebSocketLike }).WebSocket;
  return new Ctor(url, subprotocol);
}
